
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, BookOpen } from "lucide-react";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Correction ici - La détection de la page d'accueil doit inclure la route vide
  const isHomePage = location.pathname === "/" || location.pathname === "/home" || location.pathname === "/index" || location.pathname === "";

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Journalisation améliorée pour la navigation
    console.log("🧭 Header rendu sur chemin:", location.pathname, {
      currentHash: window.location.hash,
      fullUrl: window.location.href,
      origin: window.location.origin,
      isHashBasedRouting: window.location.href.includes('#/'),
      isProduction: window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1'),
      isPreviewMode: window.location.hostname.includes("lovable") || window.location.hostname.includes("preview"),
      currentImage: getLogoPath(),
      isHomePage: isHomePage
    });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, isHomePage]);

  // Correction de la classe conditionnelle pour l'arrière-plan
  const headerBgClass = isHomePage 
    ? isScrolled 
      ? "bg-white/90 backdrop-blur-sm shadow-sm" 
      : "bg-transparent"
    : "bg-white shadow-sm";

  const textColorClass = isHomePage && !isScrolled 
    ? "text-white" 
    : "text-[#00123A]";
    
  const hoverColorClass = "hover:text-[#FCACF4]";

  const menuItems = [
    { label: "Accueil", path: "/" },
    { label: "Actualités et Actions", path: "/actions" },
    { label: "La Charte", path: "/charte-republicaine" },
    { label: "Indice de la Citoyenneté\nFrançaise", path: "/indice-citoyennete" },
    { label: "Devenir Membre", path: "/adhesion" },
    { label: "Blog", path: "/blog", icon: BookOpen },
    { label: "L'association", path: "/association" },
  ];

  // Fonction pour obtenir le chemin du logo avec gestion améliorée des chemins relatifs
  function getLogoPath() {
    const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1');
    const baseUrl = isProduction ? '.' : '';
    // Utilisez une fonction qui peut gérer à la fois les chemins absolus et relatifs
    return `${baseUrl}/lovable-uploads/4e099779-9f93-42cf-8071-5264f3cec1da.png`;
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBgClass}`}>
      <nav className="container mx-auto px-4">
        <div className="flex justify-between items-center h-24">
          <Link 
            to="/" 
            className="flex items-center h-full py-2"
            onClick={() => console.log("🔗 Navigation: Accueil (/)") }
          >
            <div className="flex items-center h-full">
              <img 
                src={getLogoPath()}
                alt="Enfants de la République" 
                className="h-full max-h-20 object-contain transform transition-transform duration-300 hover:scale-105"
                onError={(e) => {
                  console.warn("⚠️ Erreur de chargement d'image:", e);
                  // Essayer un autre chemin de secours si l'image ne se charge pas
                  e.currentTarget.src = "/lovable-uploads/4e099779-9f93-42cf-8071-5264f3cec1da.png";
                  // Si toujours pas, essayer avec un chemin relatif différent
                  e.currentTarget.onerror = () => {
                    console.warn("⚠️ Deuxième tentative échouée, essai avec chemin absolu");
                    e.currentTarget.src = "https://4b99739e-d46b-4df3-8725-99b56d7abb13.lovableproject.com/lovable-uploads/4e099779-9f93-42cf-8071-5264f3cec1da.png";
                  };
                }}
              />
            </div>
          </Link>

          {/* Desktop menu - Now centered */}
          <div className="hidden md:flex items-center justify-center flex-1 space-x-8">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => console.log(`🔗 Navigation: ${item.label} (${item.path})`) }
                className={`${textColorClass} ${hoverColorClass} transition-colors font-medium pb-1 relative group flex items-center ${
                  location.pathname === item.path ? "border-b-2 border-[#00123A]" : ""
                }`}
               >
                 {item.icon && <item.icon className="mr-1 h-4 w-4" />}
                 <span className="whitespace-pre-line text-center leading-tight">{item.label}</span>
                 {location.pathname !== item.path && (
                   <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FCACF4] transition-all duration-300 group-hover:w-full"></span>
                 )}
               </Link>
            ))}
          </div>

          {/* Empty div to balance the flex layout */}
          <div className="hidden md:block w-[200px]"></div>

          {/* Mobile menu toggle button */}
          <div className="md:hidden">
            <button
              className={`p-1 rounded-md ${isHomePage && !isScrolled ? "bg-white/10 hover:bg-white/20" : "hover:bg-gray-100"} transition-colors`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X size={24} className={textColorClass} />
              ) : (
                <Menu size={24} className={textColorClass} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className={`md:hidden py-4 animate-fadeIn ${isHomePage && !isScrolled ? "bg-[#00123A]/90 rounded-lg mt-2" : "bg-white/90 backdrop-blur-sm rounded-lg mt-2"}`}>
            <div className="flex flex-col space-y-4">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    console.log(`🔗 Menu mobile: ${item.label} (${item.path})`);
                    setIsMenuOpen(false);
                  }}
                  className={`${textColorClass} ${hoverColorClass} transition-colors font-medium py-2 relative group flex items-center ${
                    location.pathname === item.path 
                      ? isHomePage && !isScrolled
                        ? "border-l-4 border-white pl-2"
                        : "border-l-4 border-[#00123A] pl-2" 
                      : "pl-3"
                  }`}
                 >
                   {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                   <span className="whitespace-pre-line leading-tight">{item.label}</span>
                   {location.pathname !== item.path && (
                     <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-[#FCACF4] transition-all duration-300 group-hover:w-full ml-3"></span>
                   )}
                 </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
